import { GET_URL } from '@utils/api/endpoints.ts';
import { Axios } from '@utils/api/axios-instance.ts';
import { useQuery, useQueryClient } from 'react-query';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';
import {
  Entitlements,
  EntitlementsResponse,
  ERROR_FALLBACK_ENTITLEMENTS
} from '@utils/types/entitlements.ts';

export const ENTITLEMENTS_QUERY_KEY = ['entitlements'];

export async function fetchEntitlements(): Promise<Entitlements> {
  const { data } = await Axios.get<EntitlementsResponse>(
    GET_URL().entitlements
  );
  return data.result;
}

/**
 * Hook to fetch organization entitlements.
 *
 * - Fetches once on login and caches for 15 minutes
 * - Disabled in on-prem mode (assumes all features available)
 * - On error, returns default entitlements (all features enabled) to avoid blocking users
 */
export function useGetEntitlements() {
  return useQuery<Entitlements>(ENTITLEMENTS_QUERY_KEY, fetchEntitlements, {
    // Disable in on-prem mode - entitlements endpoint doesn't exist
    enabled: !!TokenExtension.get()?.string && !IS_ON_PREM,
    useErrorBoundary: false,

    // Cache settings - similar to organization hook
    staleTime: 1000 * 60 * 15, // 15 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,

    // On error, don't block the app - use default entitlements
    onError: () => {
      // Error is logged by React Query, no need to block UI
    },

    // Retry once on failure
    retry: 1,
    retryDelay: 1000
  });
}

/**
 * Hook to invalidate and refetch entitlements.
 * Use after subscription changes (upgrade, downgrade, activation).
 */
export function useRefreshEntitlements() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(ENTITLEMENTS_QUERY_KEY);
  };
}

/**
 * Get entitlements data with appropriate fallback. Fail-open: only block
 * features when the API explicitly says they are off, so loading windows or
 * transient errors never lock users out of features they actually have.
 */
export function getEntitlementsWithFallback(
  data: Entitlements | undefined
): Entitlements {
  if (IS_ON_PREM) {
    return ERROR_FALLBACK_ENTITLEMENTS;
  }

  if (data) {
    return data;
  }

  return ERROR_FALLBACK_ENTITLEMENTS;
}
